import axios from 'axios';
import { handleSessionExpiration } from '../utils/sessionUtils';

// const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://13.200.162.145:8001';
const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.jcentrix.tech';

const api = axios.create({
  baseURL: API_URL,
  timeout: 10000,
  headers: { 'Content-Type': 'application/json' }
});

api.interceptors.request.use((config) => {
  // Don't add token for auth endpoints (these are public)
  const isAuthEndpoint =
    config.url?.includes('/auth/') ||
    config.url?.includes('/login') ||
    config.url?.includes('/verify-otp');

  if (!isAuthEndpoint) {
    // Check for both merchant token and admin token
    const isAdmin = config.url?.includes('/admin');

    let token = null;

    if (isAdmin) {
      token = sessionStorage.getItem('adminToken');
    } else {
      token = sessionStorage.getItem('token');
    }

    // fallback (optional safety, no break)
    if (!token) {
      token = sessionStorage.getItem('token') || sessionStorage.getItem('adminToken');
    }
    // console.log('API Request:', config.method?.toUpperCase(), config.url, 'Token found:', !!token);
    if (token && typeof token === 'string' && token.length > 10) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      //console.warn('No authentication token found in localStorage');
    }
  } else {
    //console.log('API Request:', config.method?.toUpperCase(), config.url, 'Auth endpoint - no token header');
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      handleSessionExpiration(error.response.status);
    }
    return Promise.reject({
      message: error.response?.data?.message || "Something went wrong",
      status: error.response?.status
    });
  }
);

// Admin Auth
export const adminLogin = (data) => api.post('/api/admin/login', data);

// Merchant Auth
export const sendMerchantOtp = (mobileNumber, otpType = 'LOGIN') => {
  return api.post('/api/merchant/auth/send-otp', { mobileNumber, otpType });
};
export const verifyMerchantOtp = (mobileNumber, otp) => api.post('/api/merchant/auth/verify-otp', { mobileNumber, otp: String(otp) });
export const merchantSignup = (signupData) => {
  return api.post('/api/merchant/auth/signup', { ...signupData, otp: String(signupData.otp) });
};

// Merchant Profile
export const getMerchantProfile = () => api.get('/api/merchant/profile');
export const updateMerchantProfile = (profileData) => api.put('/api/merchant/profile', profileData);

// Merchant Dashboard
export const getMerchantDashboard = () => api.get('/api/merchant/dashboard');

// Merchant Wallet Operations
export const submitWalletRecharge = (rechargeData) => {
  const { bankId, amount, transactionReference, remarks, paymentProof } = rechargeData;
  
  // Build query parameters manually to ensure proper format
  const params = new URLSearchParams();
  params.append('bankId', bankId);
  params.append('amount', amount);
  if (transactionReference) params.append('transactionReference', transactionReference);
  if (remarks) params.append('remarks', remarks);
  
  // Create FormData for file upload
  const formData = new FormData();

  if (paymentProof) {
    const maxSize = 5 * 1024 * 1024;
    if (paymentProof.size > maxSize) {
      alert("File size should be less than 5MB");
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(paymentProof.type)) {
      alert("Only JPG, PNG, or PDF files are allowed");
      return;
    }

    formData.append('paymentProof', paymentProof);
  }
  
  return api.post(`/api/merchant/wallet/recharge?${params.toString()}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};
export const getRechargeRequests = (page = 0, size = 20) => api.get(`/api/merchant/wallet/recharge-requests?page=${page}&size=${size}`);
export const getMerchantRechargePaymentProof = (requestId) =>
  api.get(`/api/merchant/wallet/recharge-requests/${requestId}/payment-proof`, { responseType: 'blob' });
export const getActiveBanks = () => api.get('/api/merchant/wallet/banks');

// Merchant KYC Management
export const getKycDocuments = () => api.get('/api/merchant/kyc/documents');
export const uploadKycDocument = (documentType, formData) => api.post(`/api/merchant/kyc/upload?documentType=${documentType}`, formData, { headers: { 'Content-Type': 'multipart/form-data' }});

// Merchant IP Management
export const getWhitelistedIps = () => api.get('/api/merchant/ips');
export const addWhitelistedIp = (data) => api.post('/api/merchant/ips', data);
export const removeWhitelistedIp = (ipId) => api.delete(`/api/merchant/ips/${ipId}`);

// Merchant API Management
export const generateApiCredentials = () => api.post('/api/merchant/credentials/generate');
export const getEnabledApis = () => api.get('/api/merchant/apis/enabled');
export const getAvailableApis = () => api.get('/api/merchant/apis/available');
export const requestApiActivation = (data) => api.post('/api/merchant/apis/request-activation', data);
export const getApiActivationRequests = (page = 0, size = 20) => api.get(`/api/merchant/apis/activation-requests?page=${page}&size=${size}`);

// Merchant Support Tickets
export const getMerchantTickets = (page = 0, size = 20) => api.get(`/api/merchant/tickets?page=${page}&size=${size}`);
export const createTicket = (data) => api.post('/api/merchant/tickets', data);
export const getTicketDetails = (ticketId) => api.get(`/api/merchant/tickets/${ticketId}`);
export const replyToTicket = (ticketId, data) => api.post(`/api/merchant/tickets/${ticketId}/reply`, data);

// Merchant Reports
export const getApiTransactions = (fromDate, toDate, apiCode = '', page = 1, pageSize = 50) => 
  api.get(`/api/merchant/reports/transactions?fromDate=${fromDate}&toDate=${toDate}&page=${page}&pageSize=${pageSize}${apiCode ? `&apiCode=${apiCode}` : ''}`);
export const getCreditCardTransactions = () => api.get('/api/merchant/reports/transactions/creditcard');
export const getBbpsTransactions = () => api.get('/api/merchant/reports/transactions/bbps');
export const getWalletBalanceWithLien = () => api.get('/api/merchant/reports/balance');
export const getAccountLedger = () => api.get('/api/merchant/reports/account_ledger');
export const getWalletLedger = (params) => {
  return api.get('/api/gateway/reports/account_ledger', { params });
};
export const getPayoutReports = (fromDate, toDate, page = 1, pageSize = 50) => 
  api.get(`/api/gateway/reports/payout?fromDate=${fromDate}&toDate=${toDate}&page=${page}&pageSize=${pageSize}`);

export const getQrTransactionReports = (fromDate, toDate, page = 1, pageSize = 50) =>
  api.get(`/api/gateway/reports/qr-transactions?fromDate=${fromDate}&toDate=${toDate}&page=${page}&pageSize=${pageSize}`);

// New Gateway Reports APIs
export const getGatewayUpiAtmReports = (fromDate, toDate, page = 1, pageSize = 50) =>
  api.get(`/api/gateway/reports/upiatm-transactions?fromDate=${fromDate}&toDate=${toDate}&page=${page}&pageSize=${pageSize}`);

export const getGatewayTransactionsByApiCode = (apiCode, fromDate, toDate, page = 1, pageSize = 50) =>
  api.get(`/api/gateway/reports/transactions/${apiCode}?fromDate=${fromDate}&toDate=${toDate}&page=${page}&pageSize=${pageSize}`);

export const getGatewayCreditCardReports = (fromDate, toDate, page = 1, pageSize = 50) =>
  api.get(`/api/gateway/reports/transactions/creditcard?fromDate=${fromDate}&toDate=${toDate}&page=${page}&pageSize=${pageSize}`);

export const getGatewayBbpsReports = (fromDate, toDate, page = 1, pageSize = 50) =>
  api.get(`/api/gateway/reports/transactions/bbps?fromDate=${fromDate}&toDate=${toDate}&page=${page}&pageSize=${pageSize}`);

export const getGatewayEpBbpsReports = (fromDate, toDate, page = 1, pageSize = 50, status = '') => {
  const params = new URLSearchParams();
  if (fromDate) params.append('fromDate', fromDate);
  if (toDate) params.append('toDate', toDate);
  if (page) params.append('page', page);
  if (pageSize) params.append('pageSize', pageSize);
  if (status && status !== 'ALL') params.append('status', status);
  return api.get(`/api/gateway/reports/ep-bbps-transactions?${params.toString()}`);
};

export const getGatewayBalance = () =>
  api.get('/api/gateway/reports/balance');

export const getGatewayAepsReports = (fromDate, toDate, page = 1, pageSize = 50) =>
  api.get(`/api/gateway/reports/aeps-transactions?fromDate=${fromDate}&toDate=${toDate}&page=${page}&pageSize=${pageSize}`);

// Admin Portal
export const getAdminDashboard = () => api.get('/api/admin/portal/dashboard');
export const getBanks = () => api.get('/api/admin/portal/banks');
export const createBank = (data) => api.post('/api/admin/portal/banks', data);
export const updateBank = (bankId, data) => api.put(`/api/admin/portal/banks/${bankId}`, data);
export const getPendingKyc = (page = 0, size = 20) => api.get(`/api/admin/portal/kyc/pending?page=${page}&size=${size}`);
export const getMerchantKycDocs = (merchantId) => api.get(`/api/admin/portal/kyc/${merchantId}/documents`);
export const approveRejectKyc = (merchantId, data) => api.post(`/api/admin/portal/kyc/${merchantId}/approve`, data);
export const getPendingRecharges = (page = 0, size = 20) => api.get(`/api/admin/portal/recharge-requests/pending?page=${page}&size=${size}`);
export const getAllRecharges = (page = 0, size = 20) => api.get(`/api/admin/portal/recharge-requests?page=${page}&size=${size}`);
export const approveRejectRecharge = (requestId, data) => api.post(`/api/admin/portal/recharge-requests/${requestId}/approve`, data);
export const getAdminRechargePaymentProof = (requestId) =>
  api.get(`/api/admin/portal/recharge-requests/${requestId}/payment-proof`, { responseType: 'blob' });
export const getPendingApiRequests = (page = 0, size = 20) => api.get(`/api/admin/portal/api-requests/pending?page=${page}&size=${size}`);
export const getAllApiRequests = (page = 0, size = 20) => api.get(`/api/admin/portal/api-requests?page=${page}&size=${size}`);
export const approveRejectApiRequest = (requestId, data) => api.post(`/api/admin/portal/api-requests/${requestId}/approve`, data);
export const getAdminTickets = (page = 0, size = 20) => api.get(`/api/admin/portal/tickets?page=${page}&size=${size}`);
export const getAdminTicketDetails = (ticketId) => api.get(`/api/admin/portal/tickets/${ticketId}`);
export const updateTicketStatus = (ticketId, data) => api.put(`/api/admin/portal/tickets/${ticketId}`, data);
export const adminReplyToTicket = (ticketId, data) => api.post(`/api/admin/portal/tickets/${ticketId}/reply`, data);

// Admin Merchant Management
export const getMerchants = (page = 1, size = 20, status = '', search = '') => {
  let url = `/api/admin/merchants?page=${page}&pageSize=${size}`;
  
  // Only add status parameter if it's not 'all' and not empty
  if (status && status !== 'all') {
    url += `&status=${status}`;
  }
  
  // Only add search parameter if it's not empty
  if (search && search.trim()) {
    url += `&search=${encodeURIComponent(search.trim())}`;
  }
  
  return api.get(url);
};
export const getMerchant = (merchantId) => api.get(`/api/admin/merchants/${merchantId}`);
// Admin Ledger Reports
export const getAdminLedger = (params) => {
  const queryParams = new URLSearchParams();
  Object.keys(params).forEach(key => {
    if (params[key] !== undefined && params[key] !== '') {
      queryParams.append(key, params[key]);
    }
  });
  return api.get(`/api/admin/ledger?${queryParams.toString()}`);
};

export const getAdminPayoutReports = (fromDate, toDate, page = 1, pageSize = 50) => 
  api.get(`/api/admin/reports/payout?fromDate=${fromDate}&toDate=${toDate}&page=${page}&pageSize=${pageSize}`);

export const getAdminQrTransactionReports = (fromDate, toDate, page = 1, pageSize = 50) =>
  api.get(`/api/admin/reports/qr-transactions?fromDate=${fromDate}&toDate=${toDate}&page=${page}&pageSize=${pageSize}`);

export const getAdminUpiAtmReports = (fromDate, toDate, page = 1, pageSize = 50, merchantId = '', status = '') => {
  const params = new URLSearchParams();
  params.append('fromDate', fromDate);
  params.append('toDate', toDate);
  params.append('page', page);
  params.append('pageSize', pageSize);
  if (merchantId) params.append('merchantId', merchantId);
  if (status && status !== 'ALL') params.append('status', status);
  return api.get(`/api/admin/reports/upiatm-transactions?${params.toString()}`);
};

export const getAdminCreditCardReports = (fromDate, toDate, page = 1, pageSize = 50, merchantId = '') => {
  const params = new URLSearchParams();
  params.append('fromDate', fromDate);
  params.append('toDate', toDate);
  params.append('page', page);
  params.append('pageSize', pageSize);
  if (merchantId) params.append('merchantId', merchantId);
  return api.get(`/api/admin/reports/creditcard?${params.toString()}`);
};

export const getAdminBbpsReports = (fromDate, toDate, page = 1, pageSize = 50, merchantId = '') => {
  const params = new URLSearchParams();
  params.append('fromDate', fromDate);
  params.append('toDate', toDate);
  params.append('page', page);
  params.append('pageSize', pageSize);
  if (merchantId) params.append('merchantId', merchantId);
  return api.get(`/api/admin/reports/bbps?${params.toString()}`);
};

export const getAdminApiLogsReport = (fromDate, toDate, page = 1, pageSize = 50, merchantId = '', status = '') => {
  const params = new URLSearchParams();
  params.append('fromDate', fromDate);
  params.append('toDate', toDate);
  params.append('page', page);
  params.append('pageSize', pageSize);
  if (merchantId) params.append('merchantId', merchantId);
  if (status && status !== 'ALL') params.append('status', status);
  return api.get(`/api/admin/reports/api-logs?${params.toString()}`);
};

export const getAdminApiLogDetail = (id) => api.get(`/api/admin/reports/api-logs/${id}`);

export const getAdminAepsReports = (fromDate, toDate, page = 1, pageSize = 50, merchantId = '', status = '') => {
  const params = new URLSearchParams();
  params.append('fromDate', fromDate);
  params.append('toDate', toDate);
  params.append('page', page);
  params.append('pageSize', pageSize);
  if (merchantId) params.append('merchantId', merchantId);
  if (status && status !== 'ALL') params.append('status', status);
  return api.get(`/api/admin/reports/aeps-transactions?${params.toString()}`);
};

export const getAdminEpBbpsReports = (fromDate, toDate, page = 1, pageSize = 50, merchantId = '', status = '') => {
  const params = new URLSearchParams();
  if (fromDate) params.append('fromDate', fromDate);
  if (toDate) params.append('toDate', toDate);
  if (page) params.append('page', page);
  if (pageSize) params.append('pageSize', pageSize);
  if (merchantId) params.append('merchantId', merchantId);
  if (status && status !== 'ALL') params.append('status', status);
  return api.get(`/api/admin/reports/ep-bbps-transactions?${params.toString()}`);
};

export const getLedgerStats = (params) => {
  const queryParams = new URLSearchParams();
  Object.keys(params).forEach(key => {
    if (params[key] !== undefined && params[key] !== '') {
      queryParams.append(key, params[key]);
    }
  });
  return api.get(`/api/admin/ledger/stats?${queryParams.toString()}`);
};

// Admin API Configuration (New comprehensive endpoints)
export const getApiConfigs = () => api.get('/api/admin/api-config');
export const getApiConfig = (apiCode) => api.get(`/api/admin/api-config/${apiCode}`);
export const createApiConfig = (data) => api.post('/api/admin/api-config', data);
export const updateApiConfig = (apiCode, data) => api.put(`/api/admin/api-config/${apiCode}`, data);
export const toggleApiStatus = (apiCode, isActive) => api.patch(`/api/admin/api-config/${apiCode}/status?isActive=${isActive}`);

// Charge Slabs
export const getChargeSlabs = (apiCode, merchantId = null) => 
  api.get(`/api/admin/api-config/${apiCode}/slabs${merchantId ? `?merchantId=${merchantId}` : ''}`);
export const createChargeSlab = (apiCode, merchantId, data) => 
  api.post(`/api/admin/api-config/${apiCode}/slabs${merchantId ? `?merchantId=${merchantId}` : ''}`, data);
export const updateChargeSlab = (slabId, data) => api.put(`/api/admin/api-config/slabs/${slabId}`, data);
export const deleteChargeSlab = (slabId) => api.delete(`/api/admin/api-config/slabs/${slabId}`);

// Merchant API Configuration
export const configureMerchantApi = (data) => api.post('/api/admin/api-config/merchant-mapping', data);
export const getMerchantApiConfig = (merchantId) => api.get(`/api/admin/merchants/${merchantId}/api-config`);

// Merchant Management for API Assignment (Removed - using main API config endpoints)
export const getAllMerchants = (page = 1, size = 1000, status = 'ACTIVE') => 
  getMerchants(page, size, status, ''); // Use the same getMerchants function that MerchantManagement uses

// Admin AEPS Settlement & Bank cache refresh
export const manualAepsSettlement = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.merchantId) queryParams.append('merchantId', params.merchantId);
  if (params.cutoffDateTime) queryParams.append('cutoffDateTime', params.cutoffDateTime);
  return api.post(`/api/admin/aeps/settle?${queryParams.toString()}`);
};

export const refreshAepsBanks = () => api.post('/api/admin/aeps/banks/refresh');

// Merchant Withdrawal
export const getMerchantBankAccount = () => api.get('/api/merchant/withdrawal/bank-account');
export const submitMerchantBankAccount = (params, cancelledCheque) => {
  const searchParams = new URLSearchParams();
  if (params.accountHolderName) searchParams.append('accountHolderName', params.accountHolderName);
  if (params.bankName) searchParams.append('bankName', params.bankName);
  if (params.accountNumber) searchParams.append('accountNumber', params.accountNumber);
  if (params.ifscCode) searchParams.append('ifscCode', params.ifscCode);
  if (params.branchName) searchParams.append('branchName', params.branchName);

  const formData = new FormData();
  if (cancelledCheque) {
    formData.append('cancelledCheque', cancelledCheque);
  }
  return api.post(`/api/merchant/withdrawal/bank-account?${searchParams.toString()}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};
export const submitWithdrawalRequest = (data) => api.post('/api/merchant/withdrawal/request', data);
export const getWithdrawalRequests = (fromDate = '', toDate = '', page = 0, pageSize = 50) => {
  let url = `/api/merchant/withdrawal/requests?page=${page}&pageSize=${pageSize}`;
  if (fromDate) url += `&fromDate=${fromDate}`;
  if (toDate) url += `&toDate=${toDate}`;
  return api.get(url);
};

// Admin Withdrawal
export const getAdminBankAccounts = (status = '', page = 0, pageSize = 50) => {
  const url = `/api/admin/withdrawal/bank-accounts?page=${page}&pageSize=${pageSize}${status ? `&status=${status}` : ''}`;
  return api.get(url);
};
export const reviewBankAccount = (id, data) => api.post(`/api/admin/withdrawal/bank-accounts/${id}/review`, data);
export const getAdminBankAccountCheque = (id) => api.get(`/api/admin/withdrawal/bank-accounts/${id}/cheque`, { responseType: 'blob' });
export const getAdminWithdrawalRequests = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.merchantId) queryParams.append('merchantId', params.merchantId);
  if (params.status && params.status !== 'ALL') queryParams.append('status', params.status);
  if (params.fromDate) queryParams.append('fromDate', params.fromDate);
  if (params.toDate) queryParams.append('toDate', params.toDate);
  if (params.page !== undefined) queryParams.append('page', params.page);
  if (params.pageSize !== undefined) queryParams.append('pageSize', params.pageSize);
  return api.get(`/api/admin/withdrawal/requests?${queryParams.toString()}`);
};
export const getAdminWithdrawalRequest = (id) => api.get(`/api/admin/withdrawal/requests/${id}`);
export const approveWithdrawalRequest = (id, data) => api.post(`/api/admin/withdrawal/requests/${id}/approve`, data);
export const rejectWithdrawalRequest = (id, data) => api.post(`/api/admin/withdrawal/requests/${id}/reject`, data);
export const transferWithdrawalRequest = (id, data) => api.post(`/api/admin/withdrawal/requests/${id}/transfer`, data);

export default api;
